import { EventBus } from "@/event-bus";
import { Location } from "@/interfaces/location";
import { Policy, Service } from "@/interfaces/roles";
import { messagesService } from "@/services/messages.service";
import cloneDeep from "lodash/cloneDeep";
import each from "lodash/each";
import get from "lodash/get";
import set from "lodash/set";
import { Component, Prop, Vue } from "vue-property-decorator";
import {
  PermissionLocationForm,
  PolicyNode,
  PolicyRawModel
} from "../declarations";
import LocationSelectComponent from "./locationSelect/locationSelect.component";
import Template from "./permissionLocation.template.vue";

@Component({
  mixins: [Template],
  components: {
    "location-select": LocationSelectComponent
  }
})
export class PermissionLocationComponent extends Vue {
  @Prop({ default: [] }) public locations!: Location[];
  @Prop({ default: [] }) public services!: Service[];
  @Prop({ default: [] }) public policies!: PolicyNode[];
  @Prop({ required: true }) public value!: PermissionLocationForm;
  @Prop({ default: [] }) public rolePolicies!: Policy[];
  @Prop({ default: false }) public onDisplay!: boolean;
  public search = "";
  public activeTab: number = 0;
  public policyModel: PolicyRawModel = {};
  public currentModel: any = null;
  public modelPath: string = "";
  public selectedLocation: Location | null = null;
  public clipPermissions: any = {};
  public currentCopied: { [key: string]: string } = {};
  public showTree = true;
  public openAll = true;
  public treeItems: { [key: string]: PolicyNode[] } = {};

  public goToStep(step: number) {
    this.$emit("goToStep", step);
    this.$emit("input", { policies: this.policyModel });
  }

  public getTabTitle(service: Service): string {
    return (this.policies.find(p => p.tag === service.id) || service).name;
  }

  public copyPermissions(service: Service) {
    this.clipPermissions[service.id] = cloneDeep(
      get(this.policyModel, this.modelPath)
    );
    this.currentCopied[service.id] = this.selectedLocation!.name;
    messagesService.renderSuccessMessage(
      this.$t("role_mgmt.copied_permissions_message", {
        locationName: this.selectedLocation!.name,
        serviceName: service.name
      }).toString()
    );
  }

  public getTabId(service: Service): string {
    if (service.id === "bdi") {
      return "btn_bio_perm";
    } else {
      return "btn_core_perm";
    }
  }

  public pastePermissions(service: Service) {
    set(
      this.policyModel,
      this.modelPath,
      cloneDeep(this.clipPermissions[service.id])
    );
    this.setCurrentModel();
    messagesService.renderSuccessMessage(
      this.$t("role_mgmt.pasted_permissions_message", {
        locationName: this.selectedLocation!.name,
        serviceName: service.name,
        copied: this.currentCopied[service.id]
      }).toString()
    );
  }

  public toggleTree() {
    this.services.forEach((_, i) => {
      const tree = this.$refs.permTree as Vue[];
      if (tree && tree.length) {
        // @ts-ignore
        tree[0].updateAll(this.openAll);
      }
    });
  }

  protected setupPolicyModel() {
    if (this.services.length) {
      this.policyModel = this.services.reduce(
        (acc: { [key: string]: any }, service: Service) => {
          if (service.has_locations) {
            acc[service.id] = this.policyModel[service.id] || {};
            each(this.locations, location => {
              if (!acc[service.id][`${location.id}`]) {
                acc[service.id][`${location.id}`] = [];
              }
            });
          } else {
            acc[service.id] = this.policyModel[service.id] || [];
          }
          return acc;
        },
        {}
      );
      this.setCurrentModel();
    }
  }

  /**
   * Adds existing policies
   * Runs when rolePolicies prop is assigned for the first time
   * this takes place on "step 1" so the policy model is initialized here,
   * given that the setupPolicyModel is reactive to what the user selects in step 1
   */
  protected async addExistingPolicies() {
    if (this.rolePolicies.length) {
      this.rolePolicies.forEach(policy => {
        const categoryAdd = this.findInTree(policy);
        if (categoryAdd) {
          if (policy.locations_count && policy.options) {
            if (!this.policyModel[policy.tag]) {
              this.policyModel[policy.tag] = {};
            }
            policy.options.forEach(option => {
              const locationId = String(option.location_id!);

              if (
                !(this.policyModel[policy.tag] as PolicyRawModel)[locationId]
              ) {
                (this.policyModel[policy.tag] as PolicyRawModel)[
                  locationId
                ] = [];
              }
              ((this.policyModel[policy.tag] as PolicyRawModel)[
                locationId
              ] as PolicyNode[]).push(categoryAdd);
            });
          } else {
            const service = this.services.find(s => s.id === policy.tag);
            if (service && !service.has_locations) {
              if (!this.policyModel[policy.tag]) {
                this.policyModel[policy.tag] = [];
              }
              // para core
              (this.policyModel[policy.tag] as PolicyNode[]).push(categoryAdd);
            }
          }
        }
      }, this);
      this.setCurrentModel();
      this.$emit("input", { policies: this.policyModel });
    }
  }

  protected findInTree(policy: Policy): PolicyNode | null {
    let found: PolicyNode | null = null;

    const app = this.policies.find(p => p.tag === policy.tag);
    found = findInChildren(policy, app!.children!);
    return found;
  }

  protected renderTree(newModel: PolicyRawModel | PolicyNode[]) {
    setTimeout(() => {
      this.currentModel = newModel;
    });
  }

  protected setCurrentModel() {
    const service = this.services[this.activeTab || 0];

    if (service.has_locations) {
      if (this.selectedLocation) {
        this.modelPath = `${service.id}.${this.selectedLocation.id}`;
      } else {
        this.showTree = false;
        return;
      }
    } else {
      this.modelPath = service.id;
    }
    const newmodel = get(this.policyModel, this.modelPath);
    this.showTree = true;
    this.currentModel = newmodel;
    this.renderTree(newmodel);
    this.$nextTick(this.toggleTree);
  }

  protected updateModel() {
    set(this.policyModel, this.modelPath, this.currentModel);
  }

  protected buildTree() {
    // Remove other key from biotrack in the top children
    const bdiIdx = this.policies.findIndex(p => p.tag === "bdi");
    this.policies[bdiIdx].children = this.policies[bdiIdx].children!.filter(
      (c: PolicyNode) => c.name !== "Other"
    );

    this.policies.forEach(policy => {
      this.$set(this.treeItems, policy.tag!, policy.children || []);
    });
  }

  protected mounted() {
    const unwatchPolicies = this.$watch("onDisplay", () => {
      if (this.onDisplay) {
        this.buildTree();
        this.addExistingPolicies();

        unwatchPolicies();
      }
    });

    EventBus.$on("rolesStepChanged", (step: number) => {
      if (step === 2) {
        this.activeTab = 0;
        this.selectedLocation = this.locations[0] || null;
        this.setupPolicyModel();
      }
    });
  }
}

const findInChildren = (
  policy: Policy,
  children: PolicyNode[]
): PolicyNode | null => {
  let found = null;
  each(children, child => {
    if (child.children) {
      found = findInChildren(policy, child.children);
    } else {
      found = child.policy_id === policy.id ? child : null;
    }
    if (found) {
      return false;
    }
  });

  return found;
};
